import React, { useState, useRef, useEffect } from 'react';
import { auth, db } from '../../firebase';
import { createUserWithEmailAndPassword, signOut } from 'firebase/auth';
import { doc, setDoc, updateDoc, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { sendWelcomeEmailApi } from '../../services/firebaseApiService';
import { tryFulfillDailyVibeEmailInvitesAfterAuth } from '../../utils/fulfillDailyVibeEmailInvitesAfterAuth';
import {
  getPendingPosterReferralForSignup,
  clearPosterReferralFromSession,
} from '../../utils/posterReferralLink';
import { isEduEmail } from '../../utils/berkeleyEmailAccess';
import { buildNewUserDoc } from '../../utils/newUserDefaults';
import { rememberEmailSignupAttachSession } from '../../utils/emailSignupCollageAttach';
import OAuthButtons from './OAuthButtons';
import '../../styles/LoginPopup.css';

const SignUpPopup = ({
  onClose,
  onShowLoginPopup,
  hint,
  signupSource = 'navbar',
  isPrelaunch = false,
  /** FoxChat-registered hooks (see App.js) — server-side collage attach for the email signup path. */
  foxChatResetEmailAttachHooksRef = null,
}) => {
  const [email, setEmail] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [termsShakeKey, setTermsShakeKey] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitDots, setSubmitDots] = useState(1);
  const [error, setError] = useState('');
  const [showWaitlist, setShowWaitlist] = useState(false);
  /** True after a non-berkeley (and non-allowlisted) signup attempt — show waitlist explainer, no account created. */
  const [showRestrictedEmailWaitlist, setShowRestrictedEmailWaitlist] = useState(false);
  const popupRef = useRef(null);
  const waitlistPopupRef = useRef(null);
  const restrictedWaitlistPopupRef = useRef(null);

  const recordSignupAccessWaitlist = async (trimmedEmail, mode) => {
    await addDoc(collection(db, 'signupAccessWaitlist'), {
      email: trimmedEmail,
      source: signupSource,
      mode,
      createdAt: serverTimestamp(),
      termsAcceptedAt: serverTimestamp(),
    });
  };

  /* Cycle "Signing up." / ".." / "..." while the request is in flight. */
  useEffect(() => {
    if (!isSubmitting) {
      setSubmitDots(1);
      return undefined;
    }
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setSubmitDots(3);
      return undefined;
    }
    const id = window.setInterval(() => {
      setSubmitDots((n) => (n % 3) + 1);
    }, 400);
    return () => window.clearInterval(id);
  }, [isSubmitting]);

  // Close when clicking outside the visible popup
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isSubmitting) return;
      let hit = false;
      if (
        popupRef.current?.contains(event.target) ||
        waitlistPopupRef.current?.contains(event.target) ||
        restrictedWaitlistPopupRef.current?.contains(event.target)
      ) {
        hit = true;
      }
      if (!hit) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose, isSubmitting]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreedToTerms) {
      setTermsShakeKey((k) => k + 1);
      return;
    }

    setIsSubmitting(true);
    setError('');

    const trimmed = email.trim().toLowerCase();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmed)) {
      setError('Please enter a valid email address (e.g., name@example.com)');
      setIsSubmitting(false);
      return;
    }

    if (isPrelaunch) {
      if (!/^[^\s@]+@berkeley\.edu$/.test(trimmed)) {
        try {
          await recordSignupAccessWaitlist(trimmed, 'prelaunch');
          setShowRestrictedEmailWaitlist(true);
        } catch (err) {
          console.error('Error saving signup access waitlist:', err);
          setError('Something went wrong. Try again.');
        } finally {
          setIsSubmitting(false);
        }
        return;
      }
      try {
        await addDoc(collection(db, 'prelaunchEmails'), {
          email: trimmed,
          source: signupSource,
          createdAt: serverTimestamp(),
          termsAcceptedAt: serverTimestamp(),
        });
        setShowWaitlist(true);
      } catch (err) {
        console.error('Error saving email:', err);
        setError('Something went wrong. Try again.');
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    const signupIsEdu = isEduEmail(trimmed);

    /* FoxChat pending save: email signup signs the account out right after creation, which
       races (and often kills) the client-side post-signup auto-save. Claim the collage NOW —
       before any auth state change — so the auto-save stays disarmed, and attach it
       server-side via the welcome email API instead. */
    const attachHooks = foxChatResetEmailAttachHooksRef?.current;
    let foxChatPoster = null;
    if (attachHooks?.claimPendingSaveForEmailSignup) {
      try {
        foxChatPoster = await attachHooks.claimPendingSaveForEmailSignup();
      } catch (claimErr) {
        console.warn('FoxChat pending-save claim failed:', claimErr);
      }
    }

    try {
      // Generate a random password for Firebase auth (user won't see this)
      const randomPassword = Math.random().toString(36).slice(-10) + Math.random().toString(36).slice(-10);
      
      // Create user with email and random password
      const userCredential = await createUserWithEmailAndPassword(auth, trimmed, randomPassword);
      const user = userCredential.user;

      // Try to create a user document in Firestore, but don't fail sign-up if this fails
      const posterReferral = getPendingPosterReferralForSignup();
      const matchFriendReferralFromFeed =
        posterReferral && posterReferral.referrerUid !== user.uid
          ? {
              referrerUid: posterReferral.referrerUid,
              posterId: posterReferral.posterId,
              capturedAt: serverTimestamp(),
            }
          : undefined;

      try {
        await setDoc(doc(db, 'users', user.uid), buildNewUserDoc({
          email: user.email,
          signupSource, // 'navbar' | 'referral' | 'feed' for welcome email
          isEdu: signupIsEdu,
          eduEmail: trimmed,
          matchFriendReferralFromFeed,
        }));
        if (matchFriendReferralFromFeed) clearPosterReferralFromSession();
        if (signupIsEdu) {
          await addDoc(collection(db, 'berkeleyVerifications'), {
            userId: user.uid,
            accountEmail: trimmed,
            berkeleyEmail: trimmed,
            status: 'pending',
            submittedAt: serverTimestamp(),
            source: 'signup',
          });
        }
      } catch (firestoreError) {
        // Log the error but don't show it to the user - the auth part succeeded
        console.error('Error creating user document:', firestoreError);
        // We'll consider sign-up successful even if Firestore write fails
      }

      // Consider the signup successful if authentication worked
      console.log('User signed up successfully:', user.email);

      // Link anonymous inline survey response to this user (if they completed survey this session)
      const lastSurveyResponseId = sessionStorage.getItem('lastSurveyResponseId');
      if (lastSurveyResponseId) {
        try {
          await updateDoc(doc(db, 'survey_responses', lastSurveyResponseId), { userId: user.uid });
          sessionStorage.removeItem('lastSurveyResponseId');
          console.log('Linked survey response to user');
        } catch (linkError) {
          console.error('Error linking survey response to user:', linkError);
        }
      }

      // Check for saved preferences after signup
      const dateIdeasPrefs = localStorage.getItem('dateIdeaPreferences');
      if (dateIdeasPrefs) {
        console.log('Found date idea preferences in localStorage after signup:', dateIdeasPrefs);
        try {
          const prefs = JSON.parse(dateIdeasPrefs);
          // Save directly to preferences field
          await setDoc(doc(db, 'users', user.uid), {
            preferences: prefs
          }, { merge: true });
          console.log('Saved date preferences to user profile after signup');
          localStorage.removeItem('dateIdeaPreferences');
        } catch (e) {
          console.error('Error saving preferences after signup:', e);
        }
      }
      
      // Send branded welcome / set-password email via Postmark (+ FoxChat collage attach:
      // we're still signed in here, so the server verifies the token email and attaches
      // the collage to this new account before the email goes out).
      try {
        const welcomeData = await sendWelcomeEmailApi(
          user.email,
          foxChatPoster ? { foxChatPoster } : undefined,
        );
        if (welcomeData?.attachedPosterId && attachHooks?.onPosterAttachedViaResetEmail) {
          try {
            attachHooks.onPosterAttachedViaResetEmail({ posterId: welcomeData.attachedPosterId });
          } catch (cbErr) {
            console.warn('onPosterAttachedViaResetEmail failed:', cbErr);
          }
        } else if (foxChatPoster && !welcomeData?.attachedPosterId) {
          console.warn('FoxChat collage was not attached during email signup');
        }
      } catch (emailError) {
        console.error('Error sending welcome email:', emailError);
      }

      await tryFulfillDailyVibeEmailInvitesAfterAuth();

      /* Keep this account's ID token for the rest of the tab so FoxChat SAVE can attach the
         collage after we sign out, without asking for the email again. */
      try {
        const idToken = await user.getIdToken();
        rememberEmailSignupAttachSession({ idToken, uid: user.uid });
      } catch (tokenErr) {
        console.warn('Could not keep signup session for collage save:', tokenErr);
      }

      // Sign out so balance/cart don't show during waitlist; user will sign in after setting password
      await signOut(auth);
      
      // Show waitlist popup instead of closing
      setShowWaitlist(true);
    } catch (error) {
      console.error('Error signing up:', error);
      /* Account creation failed — re-arm the FoxChat pending save so a later sign-in
         (e.g. via the login popup we may open next) still auto-saves the collage. */
      if (foxChatPoster && attachHooks?.restorePendingSaveAfterEmailSignup) {
        try { attachHooks.restorePendingSaveAfterEmailSignup(); } catch (_) {}
      }
      if (error.code === 'auth/email-already-in-use') {
        onClose();
        onShowLoginPopup(trimmed);
        return;
      } else if (error.code === 'auth/invalid-email') {
        setError('Please enter a valid email address (e.g., name@example.com)');
      } else {
        setError(error.message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWaitlistClose = () => {
    onClose(); // Close the entire signup experience
  };

  const handleRestrictedWaitlistClose = () => {
    onClose();
  };

  const handleTryBerkeleyEmailSignup = () => {
    setShowRestrictedEmailWaitlist(false);
    setError('');
    setEmail('');
  };

  const restrictedWaitlistParagraph1 =
    'In order to verify identity and keep our community safe, we currently allow sign\u2011ups only with berkeley.edu emails.';
  const restrictedWaitlistParagraph2 =
    "We've added you to the waitlist and we'll let you know as soon as new verification options are available.";

  return (
    <div className="login-popup-overlay signup-popup-overlay">
      {showRestrictedEmailWaitlist ? (
        <div className="login-popup signup-popup waitlist-popup" ref={restrictedWaitlistPopupRef}>
          <button type="button" className="close-button" onClick={handleRestrictedWaitlistClose}>
            ×
          </button>
          <div className="signup-restricted-waitlist-body">
            <p className="signup-restricted-waitlist-text">{restrictedWaitlistParagraph1}</p>
            <p className="signup-restricted-waitlist-text signup-restricted-waitlist-text--after-gap">
              {restrictedWaitlistParagraph2}
            </p>
          </div>
          <div className="signup-restricted-waitlist-footer">
            <button type="button" className="login-button" onClick={handleRestrictedWaitlistClose}>
              Got it
            </button>
            <div className="signup-restricted-waitlist-link-row">
              <button
                type="button"
                className="signup-restricted-berkeley-link-btn"
                onClick={handleTryBerkeleyEmailSignup}
              >
                {'Sign up with a '}
                <span className="signup-restricted-berkeley-link-btn-inline-end">
                  berkeley.edu email
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="signup-restricted-berkeley-link-arrow"
                    aria-hidden
                  >
                    <path d="M7 17 17 7M7 7h10v10" />
                  </svg>
                </span>
              </button>
            </div>
          </div>
        </div>
      ) : !showWaitlist ? (
        <div className={`login-popup signup-popup${hint ? ' signup-popup--with-hint' : ''}`} ref={popupRef}>
          <button
            className="close-button"
            onClick={onClose}
            disabled={isSubmitting}
            aria-label="Close"
          >
            ×
          </button>
          <div className="login-popup-header">
            <h2>Sign Up</h2>
            {hint ? (
              <p className="login-popup-subheader signup-popup-hint-under-title" role="note">
                {hint}
              </p>
            ) : null}
          </div>
          {!isPrelaunch && (
            <OAuthButtons
              beforeOAuth={() => {
                if (isSubmitting) return false;
                if (!agreedToTerms) {
                  setTermsShakeKey((k) => k + 1);
                  return false;
                }
                return true;
              }}
              onSuccess={onClose}
            />
          )}
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="signup-email">Email</label>
              <input
                type="email"
                id="signup-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
              />
            </div>
            {error && (
              <div className="error-message" style={{ color: '#ff4444', marginBottom: '1rem', textAlign: 'center' }}>
                {error}
              </div>
            )}
            <button
              type="submit"
              className="login-button"
              disabled={isSubmitting || !email}
              aria-busy={isSubmitting}
              aria-label={isSubmitting ? 'Signing up' : undefined}
            >
              {isSubmitting ? (
                <>
                  Signing up
                  <span className="signup-submitting-dots" aria-hidden="true">
                    {'.'.repeat(submitDots)}
                  </span>
                </>
              ) : (
                'Sign Up'
              )}
            </button>
            <label className="signup-terms-label">
              <input
                type="checkbox"
                className="signup-terms-checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
              />
              <span
                key={termsShakeKey}
                className={`signup-terms-text${termsShakeKey > 0 ? ' signup-terms-text--shake' : ''}`}
              >
                I agree to the{' '}
                <a
                  href={`/legal?from=signup&signupSource=${encodeURIComponent(signupSource)}#legal-terms`}
                >
                  Terms of Service
                </a>{' '}
                and{' '}
                <a
                  href={`/legal?from=signup&signupSource=${encodeURIComponent(signupSource)}#legal-privacy`}
                >
                  Privacy Policy
                </a>
                .
              </span>
            </label>
          </form>
        </div>
      ) : (
        <div className="login-popup signup-popup waitlist-popup" ref={waitlistPopupRef}>
          <button type="button" className="close-button" onClick={handleWaitlistClose}>
            ×
          </button>
          
          <div style={{ textAlign: 'center', padding: '1rem 0.5rem 1.5rem' }}>
            <div style={{ fontSize: '64px', marginBottom: '1rem' }}>🎉</div>
            <p style={{ fontSize: '1.1rem', lineHeight: '1.6', color: '#e0e0e0' }}>
              {isPrelaunch ? "You're on the list!" : "Thank you for signing up! Check your email to confirm your address and set your password."}
            </p>
          </div>
          
          <button type="button" className="login-button" onClick={handleWaitlistClose}>
            Got it!
          </button>
        </div>
      )}
    </div>
  );
};

export default SignUpPopup; 